import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Mail, Sparkles, ExternalLink, Smartphone, Banknote, CreditCard } from "lucide-react";
import { api } from "@/lib/api";

const VENMO_DEEP_LINK = "https://venmo.com/aviana_yoga";
const ZELLE_EMAIL = "avianayoga@gmail.com";

export default function PaymentManualConfirmation() {
  const { regId } = useParams();
  const [params] = useSearchParams();
  const method = params.get("method") || "venmo";
  const navigate = useNavigate();
  const [reg, setReg] = useState(null);

  useEffect(() => {
    api.get(`/trainings/registrations/${regId}`)
      .then(({ data }) => setReg(data))
      .catch(() => navigate("/"));
  }, [regId, navigate]);

  if (!reg) return <div className="pt-40 container-aviana text-aviana-muted">Loading…</div>;

  const isCard = method === "card";
  const Icon = isCard ? CreditCard : method === "venmo" ? Smartphone : Banknote;
  const title = isCard
    ? "Card checkout is being prepared"
    : `Send your ${reg.payment_type === "deposit" ? "deposit" : "tuition"} via ${method === "venmo" ? "Venmo" : "Zelle"}`;

  return (
    <div className="pt-28 pb-24 bg-aviana-paper" data-testid="payment-manual-page">
      <div className="container-aviana max-w-2xl">
        <div className="bg-white border border-aviana-gold/30 rounded-[2rem] p-10 text-center">
          <div className="w-14 h-14 rounded-full bg-aviana-gold/15 text-aviana-gold mx-auto mb-5 flex items-center justify-center">
            <CheckCircle2 size={26} />
          </div>
          <p className="overline-plum mb-3">{reg.offering_title}</p>
          <h1 className="font-display text-4xl text-aviana-ink leading-tight">{title}</h1>

          <div className="mt-7 bg-aviana-cream rounded-2xl p-6 text-left">
            <div className="flex items-center gap-3 mb-3">
              <Icon size={18} className="text-aviana-plum" />
              <p className="font-figure text-2xl text-aviana-ink">${reg.amount_today_usd.toLocaleString()} · {reg.payment_type === "deposit" ? "Deposit" : "Pay in full"}</p>
            </div>
            {isCard ? (
              <p className="text-aviana-inkSoft text-[14px] leading-[1.7]">
                Stripe card checkout is being wired up. For now your registration is safely saved — Holly will email you a secure link to complete payment shortly.
              </p>
            ) : method === "venmo" ? (
              <>
                <p className="text-aviana-inkSoft text-[14px] leading-[1.7]">
                  Send to <strong className="text-aviana-plum">@aviana_yoga</strong> on Venmo. Please include <strong>"{reg.full_name}"</strong> and the training name in the note so Holly can match the payment.
                </p>
                <a href={VENMO_DEEP_LINK} target="_blank" rel="noopener noreferrer" className="btn-primary !py-2.5 !px-5 !text-[11px] inline-flex items-center gap-1.5 mt-4">
                  Open Venmo <ExternalLink size={11} />
                </a>
              </>
            ) : (
              <p className="text-aviana-inkSoft text-[14px] leading-[1.7]">
                Send to <strong className="text-aviana-plum">{ZELLE_EMAIL}</strong> through your banking app. Include <strong>"{reg.full_name}"</strong> and the training name in the memo so Holly can match the payment.
              </p>
            )}
          </div>

          <div className="mt-7 text-left space-y-3">
            <Step icon={Sparkles} title="Holly receives your registration" body="She'll be notified of your form by email shortly." />
            <Step icon={Mail} title="Confirmation email on its way" body={`Once Holly receives your payment, you'll get a confirmation at ${reg.email} and your spot is officially held.`} />
            {reg.balance_due_usd > 0 && (
              <Step icon={CheckCircle2} title={`Balance: $${reg.balance_due_usd.toLocaleString()} due before training`} body="Holly will send a reminder one week before your training starts." />
            )}
          </div>

          <p className="text-[12px] text-aviana-muted mt-10">
            Questions? <a href="mailto:holly@avianayoga.com" className="text-aviana-plum underline underline-offset-2">holly@avianayoga.com</a>
          </p>
          <Link to="/" className="block mt-3 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink">← Back to home</Link>
        </div>
      </div>
    </div>
  );
}

function Step({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-3 bg-aviana-cream/40 rounded-xl p-4">
      <div className="w-8 h-8 rounded-full bg-aviana-plum/10 text-aviana-plum flex items-center justify-center flex-shrink-0">
        <Icon size={14} />
      </div>
      <div>
        <p className="font-medium text-aviana-ink text-[14px]">{title}</p>
        <p className="text-aviana-muted text-[13px] leading-[1.6]">{body}</p>
      </div>
    </div>
  );
}
